import { Component, OnDestroy } from "@angular/core";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import {
  DestinationMetadataOverride,
  PublishMetadata,
  SocialAccount,
} from "app/js/src/domain/clipping-studio/publish/publish-models";
import { buildPublicationSettingsSummaryLines } from "app/js/src/domain/clipping-studio/publish/publish-settings-summary";

export interface PublishPreviewCardVm {
  account: SocialAccount;
  aspectRatio: string;
  title: string;
  coverUrl: string | null;
  settingsLines: string[];
}

@Component({
  selector: "evrideo-clipping-studio-publish-tab",
  templateUrl: "./clipping-studio-publish-tab.component.html",
  styleUrls: ["./clipping-studio-publish-tab.component.scss"],
})
export class ClippingStudioPublishTabComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  /** Same source as Destinations tab (`selectedDestinations$`). */
  selectedCount = 0;
  previewCards: PublishPreviewCardVm[] = [];

  constructor(private publishState: PublishStateService) {
    combineLatest([
      this.publishState.selectedDestinations$,
      this.publishState.metadata$,
      this.publishState.destinationMetadataOverrides$,
      this.publishState.configs$,
      this.publishState.publishModel$,
      this.publishState.destinationSettingsUiRevision$,
      this.publishState.projectClipPreview$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([selectedIds, metadata, overrides]) => {
        this.selectedCount = selectedIds?.length ?? 0;
        this.previewCards = this.buildPreviewCards(
          selectedIds ?? [],
          metadata,
          overrides ?? {},
        );
      });
  }

  get hasNoDestinationsSelected(): boolean {
    return this.selectedCount === 0;
  }

  get readyToPublishLabel(): string {
    const n = this.selectedCount;
    const unit = n === 1 ? "destination" : "destinations";
    return `${n} ${unit} ready to publish`;
  }

  trackByDestinationId(_index: number, card: PublishPreviewCardVm): string {
    return card.account.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildPreviewCards(
    selectedIds: string[],
    metadata: PublishMetadata | null,
    overrides: Record<string, DestinationMetadataOverride>,
  ): PublishPreviewCardVm[] {
    const catalog = this.publishState.getDestinations();
    const byId = new Map(catalog.map((a) => [a.id, a]));

    return selectedIds
      .map((id) => byId.get(id))
      .filter((account): account is SocialAccount => !!account)
      .filter((account) =>
        account.type === "fb" ||
        account.type === "ig" ||
        account.type === "yt" ||
        account.type === "tt",
      )
      .map((account) => {
        const aspectRatio = this.publishState.getConfig(account.id).aspectRatio;
        const title = this.resolveTitle(account.id, metadata, overrides);
        const coverUrl = this.publishState.getPublicationCoverDisplayUrl(account.id);
        const { settings, youtubeCategoryTitle } =
          this.publishState.getEffectivePublicationSettingsForPreview(
            account.id,
            account.type,
          );
        const settingsLines = buildPublicationSettingsSummaryLines({
          type: account.type,
          settings,
          youtubeCategoryTitle,
        });

        return {
          account,
          aspectRatio,
          title,
          coverUrl,
          settingsLines,
        };
      });
  }

  private resolveTitle(
    destinationId: string,
    metadata: PublishMetadata | null,
    overrides: Record<string, DestinationMetadataOverride>,
  ): string {
    const overrideTitle = overrides[destinationId]?.title;
    const raw =
      overrideTitle !== undefined ? overrideTitle : metadata?.title ?? "";
    const trimmed = raw.trim();
    return trimmed || "Untitled";
  }
}
